/**
 * Ledger-derived QA readiness helper.
 * ─────────────────────────────────────────────────────────────────────────
 * Pure function over the real installer execution ledger
 * (`installer_opportunities` + `execution_proofs`). No writes,
 * no merging with the prototype `JobSpecExecution.photos[]` model.
 *
 * Used by JobDetailCockpit → Quality tab ("Execution Integrity") to decide
 * whether QA review UI should mount based on real website-submitted work,
 * independent of in-memory prototype phase state.
 *
 * Submission detection (intentionally generous, ledger source of truth):
 *   • status === "submitted_for_review"  →  installer submitted for QA
 *   • status === "completed"             →  already QA-passed (still show)
 *   • status === "callback_flagged"      →  needs reviewer attention
 *   • submitted_at !== null              →  defensive fallback
 *
 * Reasoning copy is short and beta-grade. Callers map keys to UI.
 */
import type {
  ExecutionProof,
  ExecutionStatus,
  PortalExecution,
} from "@/lib/supabase/executions";

export type QaReadinessReasonKey =
  | "no_real_execution"
  | "submitted_for_review"
  | "completed"
  | "callback_flagged"
  | "proofs_no_submission"
  | "execution_in_progress";

export interface LedgerQaReadiness {
  /** At least one portal opportunity row for this job. */
  hasRealExecution: boolean;
  /** At least one execution in a reviewer-ready status. */
  hasSubmittedExecution: boolean;
  /** At least one non-superseded proof uploaded from the website. */
  hasProofs: boolean;
  /** Status of the most-recently-assigned execution, or null. */
  latestExecutionStatus: ExecutionStatus | null;
  /** Count of non-superseded proofs across all executions for this job. */
  proofCount: number;
  /** Mount QA review UI when true. */
  readyForQaReview: boolean;
  /** Stable key for UI mapping. */
  reasonKey: QaReadinessReasonKey;
  /** Plain-language reason suitable for inline help text. */
  reason: string;
}

const REVIEWER_READY: ReadonlySet<ExecutionStatus> = new Set<ExecutionStatus>([
  "submitted_for_review",
  "completed",
  "callback_flagged",
]);

function isReviewerReady(exec: PortalExecution): boolean {
  if (REVIEWER_READY.has(exec.status)) return true;
  // Defensive: a non-null submitted_at without the canonical status flip
  // still implies the installer pressed submit.
  return Boolean(exec.submitted_at);
}

/**
 * Pure derivation — never throws, never fetches. Pass in the lists that
 * `useExecutionProofs` already returns.
 */
export function deriveLedgerQaReadiness(
  executions: PortalExecution[],
  proofs: ExecutionProof[],
): LedgerQaReadiness {
  const liveProofs = proofs.filter((p) => p.superseded_by === null);
  const proofCount = liveProofs.length;
  const hasProofs = proofCount > 0;

  if (executions.length === 0) {
    return {
      hasRealExecution: false,
      hasSubmittedExecution: false,
      hasProofs,
      latestExecutionStatus: null,
      proofCount,
      readyForQaReview: false,
      reasonKey: "no_real_execution",
      reason: hasProofs
        ? "Proofs uploaded, but no live execution record exists yet."
        : "No live execution activity for this job yet.",
    };
  }

  // `listExecutionsByAppJobId` already orders by assigned_at DESC, but we
  // re-sort defensively so the helper is pure and ordering-independent.
  const sorted = [...executions].sort((a, b) =>
    b.assigned_at.localeCompare(a.assigned_at),
  );
  const latest = sorted[0];
  const submitted = sorted.find(isReviewerReady) ?? null;
  const hasSubmittedExecution = submitted !== null;

  if (hasSubmittedExecution) {
    const status = submitted!.status;
    const reasonKey: QaReadinessReasonKey =
      status === "completed"
        ? "completed"
        : status === "callback_flagged"
          ? "callback_flagged"
          : "submitted_for_review";
    const reason =
      reasonKey === "completed"
        ? "Execution completed — ledger record available for QA review."
        : reasonKey === "callback_flagged"
          ? "Installer flagged a callback — reviewer attention required."
          : "Installer submitted execution for QA review.";
    return {
      hasRealExecution: true,
      hasSubmittedExecution: true,
      hasProofs,
      latestExecutionStatus: latest.status,
      proofCount,
      readyForQaReview: true,
      reasonKey,
      reason,
    };
  }

  return {
    hasRealExecution: true,
    hasSubmittedExecution: false,
    hasProofs,
    latestExecutionStatus: latest.status,
    proofCount,
    readyForQaReview: false,
    reasonKey: hasProofs ? "proofs_no_submission" : "execution_in_progress",
    reason: hasProofs
      ? "Proofs uploaded — not submitted for QA yet."
      : "Execution in progress — no proofs submitted yet.",
  };
}
